/**
 * Workspace Page
 * ==============
 * Main workspace with tabs: Saved, Projects, Moodboards
 * 
 * NOT a dashboard. A working environment.
 * Focus on actions, not metrics.
 * 
 * Refined UX:
 * - Clear navigation back to Explorer
 * - Smooth transitions
 * - Visual feedback on actions
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useBlueprint } from '../context/BlueprintContext';
import { useAuth } from '../App';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  Heart, Folder, Grid3X3, Plus, Trash2, Search,
  ArrowRight, ArrowLeft, Eye, Image as ImageIcon, 
  Check, X, Loader2, Pencil
} from 'lucide-react';
import { 
  getSavedMaterials, 
  getProjects, 
  getMoodboards,
  unsaveMaterial,
  deleteProject,
  deleteMoodboard,
  createProject,
  createMoodboard,
  updateProject
} from '../services/workspaceService';

// ============================================================================
// SAVED MATERIALS TAB
// ============================================================================

const SavedMaterialsTab = ({ materials, onRemove, getRoute, buildRoute, onRefresh }) => {
  const [removing, setRemoving] = useState(null);
  
  const handleRemove = async (materialId) => {
    setRemoving(materialId);
    await onRemove(materialId);
    setRemoving(null);
  };
  
  if (materials.length === 0) {
    return (
      <div className="text-center py-20">
        <div className="w-20 h-20 rounded-full bg-[#1A1A18] border border-[#2A2A28] flex items-center justify-center mx-auto mb-6">
          <Heart size={32} className="text-[#444]" />
        </div>
        <h3 className="text-white text-xl font-medium mb-3">Nessun materiale salvato</h3>
        <p className="text-[#888] text-sm mb-8 max-w-sm mx-auto">
          Esplora la nostra collezione e salva i materiali che ti interessano per ritrovarli qui.
        </p>
        <Link 
          to={getRoute('explorer', '/materials')} 
          className="inline-flex items-center gap-2 px-6 py-3 bg-[var(--gold)] text-white font-medium rounded-lg hover:bg-[var(--gold-lt)] transition-colors"
        >
          <Search size={18} />
          Esplora Materiali
        </Link>
      </div>
    );
  }
  
  return (
    <div>
      {/* Quick stats */}
      <div className="flex items-center justify-between mb-6">
        <p className="text-[#888] text-sm">
          {materials.length} material{materials.length !== 1 ? 'i' : 'e'} salvat{materials.length !== 1 ? 'i' : 'o'}
        </p>
        <Link 
          to={getRoute('explorer', '/materials')}
          className="text-sm text-[var(--gold)] hover:underline flex items-center gap-1"
        >
          Aggiungi altri
          <ArrowRight size={14} />
        </Link>
      </div>
      
      {/* Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {materials.map((item) => (
          <div 
            key={item.id} 
            className={`group relative transition-all duration-300 ${removing === item.material_id ? 'opacity-50 scale-95' : ''}`}
          >
            <Link
              to={buildRoute('material_detail', { slug: item.material_id })}
              className="block aspect-square rounded-xl overflow-hidden bg-[#1A1A18] border border-[#2A2A28] hover:border-[var(--gold)]/50 transition-colors"
            >
              {item.material_image ? (
                <img
                  src={item.material_image}
                  alt={item.material_name || 'Material'}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ImageIcon size={32} className="text-[#444]" />
                </div>
              )}
              
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                  <Eye size={24} className="text-white" />
                </div>
              </div>
            </Link>
            
            {/* Info */}
            <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/90 via-black/60 to-transparent rounded-b-xl">
              <p className="text-white text-sm font-medium truncate">
                {item.material_name || 'Materiale'}
              </p>
              {item.material_type && (
                <p className="text-[#999] text-xs">{item.material_type}</p>
              )}
            </div>
            
            {/* Remove Button */}
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleRemove(item.material_id);
              }}
              disabled={removing === item.material_id}
              className="absolute top-2 right-2 p-2 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-all hover:bg-red-500 disabled:opacity-50"
              title="Rimuovi dai salvati"
            >
              {removing === item.material_id ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <X size={14} />
              )}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// PROJECTS TAB
// ============================================================================

const ProjectsTab = ({ projects, onDelete, onCreate, onUpdate, navigate, buildRoute, getRoute }) => {
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [editing, setEditing] = useState(null); // Project ID being edited
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);
  const [limitError, setLimitError] = useState(null); // P3: Limit error state
  
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    
    setCreating(true);
    setLimitError(null);
    
    const result = await onCreate({ name: newName.trim() });
    
    // P3: Handle limit reached
    if (result.limitReached) {
      setLimitError(result.error);
      setCreating(false);
      return;
    }
    
    if (result.success) {
      setNewName('');
      setShowCreate(false);
    }
    setCreating(false);
  };
  
  const handleDelete = async (projectId) => {
    if (!confirm('Eliminare questo progetto?')) return;
    setDeleting(projectId);
    await onDelete(projectId);
    setDeleting(null);
  };
  
  const startEdit = (project) => {
    setEditing(project.id);
    setEditName(project.name);
  };
  
  const cancelEdit = () => {
    setEditing(null);
    setEditName('');
  };
  
  const saveEdit = async (projectId) => {
    if (!editName.trim()) return;
    
    setSaving(true);
    await onUpdate(projectId, { name: editName.trim() });
    setEditing(null);
    setEditName('');
    setSaving(false);
  };
  
  return (
    <div>
      {/* P3: Limit Error Banner */}
      {limitError && (
        <div className="mb-6 p-4 bg-orange-500/10 border border-orange-500/30 rounded-xl flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center flex-shrink-0">
            <Folder size={16} className="text-orange-400" />
          </div>
          <div className="flex-1">
            <p className="text-orange-300 text-sm font-medium">Limite raggiunto</p>
            <p className="text-orange-200/70 text-sm mt-1">{limitError}</p>
            <Link 
              to="/pricing" 
              className="inline-flex items-center gap-1 text-[var(--gold)] text-sm font-medium mt-2 hover:underline"
            >
              Vedi piani disponibili
              <ArrowRight size={14} />
            </Link>
          </div>
          <button 
            onClick={() => setLimitError(null)}
            className="p-1 text-orange-400 hover:text-orange-300"
          >
            <X size={16} />
          </button>
        </div>
      )}
      
      {/* Create Section */}
      <div className="mb-8">
        {showCreate ? (
          <form onSubmit={handleCreate} className="flex gap-3 max-w-lg">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Nome progetto"
              className="flex-1 px-4 py-3 bg-[#0A0A09] border border-[#3A3A38] rounded-lg text-white placeholder-[#666] focus:border-[var(--gold)] focus:outline-none transition-colors"
              autoFocus
            />
            <button
              type="submit"
              disabled={!newName.trim() || creating}
              className="px-6 py-3 bg-[var(--gold)] text-white rounded-lg font-medium disabled:opacity-50 flex items-center gap-2 transition-all hover:bg-[var(--gold-lt)]"
            >
              {creating ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
              Crea
            </button>
            <button
              type="button"
              onClick={() => {
                setShowCreate(false);
                setNewName('');
                setLimitError(null);
              }}
              className="px-4 py-3 text-[#888] hover:text-white transition-colors"
            >
              Annulla
            </button>
          </form>
        ) : (
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-3 px-5 py-3 border border-dashed border-[#3A3A38] rounded-xl text-[#888] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-all group"
          >
            <Plus size={20} className="group-hover:rotate-90 transition-transform" />
            <span className="font-medium">Nuovo Progetto</span>
          </button>
        )}
      </div>
      
      {/* Empty State */}
      {projects.length === 0 && !showCreate && (
        <div className="text-center py-16">
          <div className="w-20 h-20 rounded-full bg-[#1A1A18] border border-[#2A2A28] flex items-center justify-center mx-auto mb-6">
            <Folder size={32} className="text-[#444]" />
          </div>
          <h3 className="text-white text-xl font-medium mb-3">Nessun progetto</h3>
          <p className="text-[#888] text-sm max-w-sm mx-auto">
            Crea un progetto per organizzare i tuoi materiali per cliente o spazio.
          </p>
        </div>
      )}
      
      {/* Projects Grid */}
      {projects.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {projects.map((project) => (
            <div
              key={project.id}
              className={`group bg-[#1A1A18] border border-[#2A2A28] rounded-xl overflow-hidden hover:border-[var(--gold)]/50 transition-all ${editing !== project.id ? 'cursor-pointer' : ''} ${deleting === project.id ? 'opacity-50 scale-98' : ''}`}
              onClick={() => editing !== project.id && navigate(buildRoute('project_detail', { id: project.id }))}
            >
              {/* Preview Images */}
              <div className="flex gap-0.5 h-24 bg-[#0A0A09]">
                {project.project_materials?.slice(0, 3).map((pm, i) => (
                  <div key={i} className="flex-1 overflow-hidden">
                    {pm.material_image ? (
                      <img
                        src={pm.material_image}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-[#1A1A18]" />
                    )}
                  </div>
                ))}
                {(!project.project_materials || project.project_materials.length === 0) && (
                  <div className="flex-1 flex items-center justify-center">
                    <Folder size={28} className="text-[#333]" />
                  </div>
                )}
              </div>
              
              {/* Info */}
              <div className="p-4">
                {editing === project.id ? (
                  // Edit mode
                  <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="flex-1 px-3 py-1.5 bg-[#0A0A09] border border-[#3A3A38] rounded text-white text-sm focus:border-[var(--gold)] focus:outline-none"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveEdit(project.id);
                        if (e.key === 'Escape') cancelEdit();
                      }}
                    />
                    <button
                      onClick={() => saveEdit(project.id)}
                      disabled={!editName.trim() || saving}
                      className="p-1.5 text-[var(--gold)] hover:bg-[var(--gold)]/10 rounded disabled:opacity-50"
                    >
                      {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="p-1.5 text-[#888] hover:text-white"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  // View mode
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <h3 className="text-white font-medium truncate group-hover:text-[var(--gold)] transition-colors">
                        {project.name}
                      </h3>
                      <p className="text-[#888] text-sm mt-1">
                        {project.project_materials?.length || 0} materiali
                      </p>
                    </div>
                    
                    {/* Quick Actions - visible on hover */}
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          startEdit(project);
                        }}
                        className="p-2 text-[#666] hover:text-[var(--gold)] transition-colors"
                        title="Rinomina"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(project.id);
                        }}
                        disabled={deleting === project.id}
                        className="p-2 text-[#666] hover:text-red-500 transition-colors"
                        title="Elimina"
                      >
                        {deleting === project.id ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <Trash2 size={14} />
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// MOODBOARDS TAB
// ============================================================================

const MoodboardsTab = ({ moodboards, onDelete, onCreate }) => {
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [limitError, setLimitError] = useState(null); // P3: Limit error state
  
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    
    setCreating(true);
    setLimitError(null);
    
    const result = await onCreate({ name: newName.trim() });
    
    // P3: Handle limit reached
    if (result.limitReached) {
      setLimitError(result.error);
      setCreating(false);
      return;
    }
    
    if (result.success) {
      setNewName('');
      setShowCreate(false);
    }
    setCreating(false);
  };
  
  const handleDelete = async (moodboardId) => {
    if (!confirm('Eliminare questa moodboard?')) return;
    setDeleting(moodboardId);
    await onDelete(moodboardId);
    setDeleting(null);
  };
  
  return (
    <div>
      {/* P3: Limit Error Banner */}
      {limitError && (
        <div className="mb-6 p-4 bg-orange-500/10 border border-orange-500/30 rounded-xl flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center flex-shrink-0">
            <Grid3X3 size={16} className="text-orange-400" />
          </div>
          <div className="flex-1">
            <p className="text-orange-300 text-sm font-medium">Limite raggiunto</p>
            <p className="text-orange-200/70 text-sm mt-1">{limitError}</p>
            <Link 
              to="/pricing" 
              className="inline-flex items-center gap-1 text-[var(--gold)] text-sm font-medium mt-2 hover:underline"
            >
              Vedi piani disponibili
              <ArrowRight size={14} />
            </Link>
          </div>
          <button 
            onClick={() => setLimitError(null)}
            className="p-1 text-orange-400 hover:text-orange-300"
          >
            <X size={16} />
          </button>
        </div>
      )}
      
      {/* Create Section */}
      <div className="mb-8">
        {showCreate ? (
          <form onSubmit={handleCreate} className="flex gap-3 max-w-lg">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Nome moodboard"
              className="flex-1 px-4 py-3 bg-[#0A0A09] border border-[#3A3A38] rounded-lg text-white placeholder-[#666] focus:border-[var(--gold)] focus:outline-none transition-colors"
              autoFocus
            />
            <button
              type="submit"
              disabled={!newName.trim() || creating}
              className="px-6 py-3 bg-[var(--gold)] text-white rounded-lg font-medium disabled:opacity-50 flex items-center gap-2"
            >
              {creating ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
              Crea
            </button>
            <button
              type="button"
              onClick={() => {
                setShowCreate(false);
                setNewName('');
                setLimitError(null);
              }}
              className="px-4 py-3 text-[#888] hover:text-white transition-colors"
            >
              Annulla
            </button>
          </form>
        ) : (
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-3 px-5 py-3 border border-dashed border-[#3A3A38] rounded-xl text-[#888] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-all group"
          >
            <Plus size={20} className="group-hover:rotate-90 transition-transform" />
            <span className="font-medium">Nuova Moodboard</span>
          </button>
        )}
      </div>
      
      {/* Empty State */}
      {moodboards.length === 0 && !showCreate && (
        <div className="text-center py-16">
          <div className="w-20 h-20 rounded-full bg-[#1A1A18] border border-[#2A2A28] flex items-center justify-center mx-auto mb-6">
            <Grid3X3 size={32} className="text-[#444]" />
          </div>
          <h3 className="text-white text-xl font-medium mb-3">Nessuna moodboard</h3>
          <p className="text-[#888] text-sm max-w-sm mx-auto">
            Crea una moodboard per visualizzare e confrontare le tue selezioni.
          </p>
        </div>
      )}
      
      {/* Moodboards Grid */}
      {moodboards.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {moodboards.map((board) => (
            <div
              key={board.id}
              className={`group bg-[#1A1A18] border border-[#2A2A28] rounded-xl overflow-hidden hover:border-[var(--gold)]/50 transition-all ${deleting === board.id ? 'opacity-50' : ''}`}
            >
              {/* Preview Grid - Pinterest style */}
              <div className="aspect-video grid grid-cols-3 grid-rows-2 gap-0.5 bg-[#0A0A09]">
                {board.moodboard_items?.slice(0, 6).map((item, i) => (
                  <div key={i} className="bg-[#1A1A18] overflow-hidden">
                    {item.material_image && (
                      <img
                        src={item.material_image}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                ))}
                {Array.from({ length: Math.max(0, 6 - (board.moodboard_items?.length || 0)) }).map((_, i) => (
                  <div key={`empty-${i}`} className="bg-[#1A1A18]" />
                ))}
              </div>
              
              {/* Info */}
              <div className="p-4 flex items-center justify-between">
                <div>
                  <h3 className="text-white font-medium group-hover:text-[var(--gold)] transition-colors">
                    {board.name}
                  </h3>
                  <p className="text-[#888] text-sm">
                    {board.moodboard_items?.length || 0} elementi
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(board.id);
                  }}
                  disabled={deleting === board.id}
                  className="p-2 text-[#666] hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                >
                  {deleting === board.id ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Trash2 size={16} />
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// MAIN WORKSPACE PAGE
// ============================================================================

const WorkspacePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getContent, getRoute, buildRoute } = useBlueprint();
  const { user, isDemo } = useAuth();
  
  // State
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'saved');
  const [savedMaterials, setSavedMaterials] = useState([]);
  const [projects, setProjects] = useState([]);
  const [moodboards, setMoodboards] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Tabs config
  const tabs = [
    { id: 'saved', label: 'Salvati', icon: Heart, count: savedMaterials.length },
    { id: 'projects', label: 'Progetti', icon: Folder, count: projects.length },
    { id: 'moodboards', label: 'Moodboard', icon: Grid3X3, count: moodboards.length }
  ];
  
  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    const [saved, proj, boards] = await Promise.all([
      getSavedMaterials(),
      getProjects(),
      getMoodboards()
    ]);
    setSavedMaterials(saved);
    setProjects(proj);
    setMoodboards(boards);
    setLoading(false);
  }, []);
  
  useEffect(() => {
    loadData();
  }, [loadData]);
  
  // Sync tab with URL
  useEffect(() => {
    setSearchParams({ tab: activeTab }, { replace: true });
  }, [activeTab, setSearchParams]);
  
  // Handlers
  const handleRemoveSaved = async (materialId) => {
    await unsaveMaterial(materialId);
    setSavedMaterials(prev => prev.filter(m => m.material_id !== materialId));
  };
  
  const handleDeleteProject = async (projectId) => {
    await deleteProject(projectId);
    setProjects(prev => prev.filter(p => p.id !== projectId));
  };
  
  const handleUpdateProject = async (projectId, updates) => {
    const result = await updateProject(projectId, updates);
    if (result.success) {
      setProjects(prev => prev.map(p => 
        p.id === projectId ? { ...p, ...updates } : p
      ));
    }
    return result;
  };
  
  const handleDeleteMoodboard = async (moodboardId) => {
    await deleteMoodboard(moodboardId);
    setMoodboards(prev => prev.filter(m => m.id !== moodboardId));
  };
  
  const handleCreateProject = async (data) => {
    const result = await createProject(data);
    if (result.success) {
      setProjects(prev => [result.data, ...prev]);
    }
    return result;
  };
  
  const handleCreateMoodboard = async (data) => {
    const result = await createMoodboard(data);
    if (result.success) {
      setMoodboards(prev => [result.data, ...prev]);
    }
    return result;
  };
  
  return (
    <div className="min-h-screen bg-[#0A0A09]">
      <Header />
      
      <main className="pt-[68px]">
        {/* Demo Mode Banner - subtle suggestion to login */}
        {isDemo && (
          <div className="bg-[#1A1A18] border-b border-[#2A2A28] py-3 px-6">
            <div className="max-w-7xl mx-auto flex items-center justify-between">
              <p className="text-[#888] text-sm">
                <span className="text-[var(--gold)]">Demo Mode</span> — I tuoi dati sono salvati localmente
              </p>
              <Link 
                to={getRoute('login', '/login')}
                className="text-sm text-[var(--gold)] hover:underline flex items-center gap-1"
              >
                Accedi per sincronizzare
                <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        )}
        
        {/* Header */}
        <div className="border-b border-[#1A1A18]">
          <div className="max-w-7xl mx-auto px-6 py-8">
            {/* Top Row: Back + Title + User */}
            <div className="flex items-center justify-between mb-6">
              {/* Back to Explorer */}
              <Link 
                to={getRoute('explorer', '/materials')}
                className="flex items-center gap-2 text-[#888] hover:text-[var(--gold)] transition-colors group"
              >
                <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
                <span className="text-sm font-medium">Esplora</span>
              </Link>
              
              {/* User Info */}
              <div className="flex items-center gap-3">
                {isDemo ? (
                  <Link 
                    to={getRoute('login', '/login')}
                    className="px-4 py-2 bg-[var(--gold)] text-white text-sm font-medium rounded-lg hover:bg-[var(--gold-lt)] transition-colors"
                  >
                    Accedi
                  </Link>
                ) : (
                  <>
                    <div className="text-right hidden sm:block">
                      <p className="text-white text-sm font-medium">{user?.email}</p>
                      <p className="text-[#666] text-xs capitalize">{user?.plan || 'Free'}</p>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-[var(--gold)] flex items-center justify-center text-white font-bold text-sm">
                      {user?.email?.charAt(0).toUpperCase() || 'U'}
                    </div>
                  </>
                )}
              </div>
            </div>
            
            {/* Title */}
            <div className="mb-8">
              <h1 className="text-3xl md:text-4xl font-[var(--ff-h)] font-semibold text-white">
                Workspace
              </h1>
              <p className="text-[#888] text-sm mt-2">
                I tuoi materiali salvati, progetti e moodboard
              </p>
            </div>
            
            {/* Tabs */}
            <div className="flex gap-1 overflow-x-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      flex items-center gap-2 px-5 py-3 font-medium text-sm transition-all whitespace-nowrap
                      ${isActive 
                        ? 'bg-[#1A1A18] text-white border-t-2 border-t-[var(--gold)] rounded-t-lg' 
                        : 'text-[#888] hover:text-white'
                      }
                    `}
                  >
                    <Icon size={16} className={isActive ? 'text-[var(--gold)]' : ''} />
                    {tab.label}
                    {tab.count > 0 && (
                      <span className={`
                        px-2 py-0.5 rounded-full text-xs font-medium
                        ${isActive ? 'bg-[var(--gold)] text-white' : 'bg-[#2A2A28] text-[#888]'}
                      `}>
                        {tab.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="max-w-7xl mx-auto px-6 py-8">
          {loading ? (
            <div className="text-center py-20">
              <div className="w-14 h-14 border-2 border-[var(--gold)] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-[#888]">Caricamento...</p>
            </div>
          ) : (
            <div className="min-h-[400px]">
              {activeTab === 'saved' && (
                <SavedMaterialsTab 
                  materials={savedMaterials} 
                  onRemove={handleRemoveSaved}
                  onRefresh={loadData}
                  getRoute={getRoute}
                  buildRoute={buildRoute}
                />
              )}
              
              {activeTab === 'projects' && (
                <ProjectsTab 
                  projects={projects}
                  onDelete={handleDeleteProject}
                  onCreate={handleCreateProject}
                  onUpdate={handleUpdateProject}
                  navigate={navigate}
                  buildRoute={buildRoute}
                  getRoute={getRoute}
                />
              )}
              
              {activeTab === 'moodboards' && (
                <MoodboardsTab 
                  moodboards={moodboards}
                  onDelete={handleDeleteMoodboard}
                  onCreate={handleCreateMoodboard}
                />
              )}
            </div>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default WorkspacePage;
